import PageLayout from '@/components/shared/PageLayout';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import type { PostureAnalysis, Client, AnalysisResult, View } from "@/lib/types";
import { useAuth } from '@/contexts/AuthContext';
import { getClient } from '@/components/clients/services/clients-api';
import { ChevronLeft, Download } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import { drawCustomPelvis, drawLandmarks, drawPlumb } from "@/components/posture-analysis/lib/drawing";
import { analyzeAnterior, analyzePosterior, analyzeLateral } from "@/components/posture-analysis/lib/analysis";
import { rotateLandmarks, rotateCustomLandmarks } from "@/lib/posture-analysis/floor-correction";
import { pickManualFloorCorrection } from "@/components/posture-analysis/lib/build-captured-views-for-save";
import { exportPostureAnalysisToPdf } from "@/lib/pdf-export";
import { useToast } from "@/hooks/use-toast";
import { evaluateFullPostureAnalysis } from "@/lib/posture-analysis";
import { filterMetricsByExperimental } from "@/components/posture-analysis/lib/posture-metrics-filter";
import { captureLacksLateralView, LATERAL_VIEW_REQUIRED_NOTICE } from '@/components/posture-analysis/lib/lateral-view-notice';
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateSafe, formatDateLocale, formatDateTimeLocale, safeToDate, cn } from "@/lib/utils";
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import { ContextualToolbar, ToolbarButtonGroup } from "@/components/ui/contextual-toolbar";
import { AnalysisReportTable } from "@/components/posture-analysis/AnalysisPanels";
import { parseAssessmentValues } from '@/components/sop-intake/sop-analyzer/engine/parse-values';
import { getSopAssessments } from '@/components/sop-intake/sop-analyzer/constants';
import { FullScreenImageDialog } from "@/components/ui/full-screen-image-dialog";
import type { CaptureSequence } from '@/components/posture-analysis/types';
import {
  getClientPostureAnalyses,
  getPostureAnalysis,
  updatePostureAnalysis,
} from '@/components/posture-analysis/services/posture-analyses-api';
import { getClientAssessments } from '@/components/sop-intake/services/assessments-api';
import { loadPostureSettings, loadPostureSettingsFromLocalStorage } from '@/components/posture-analysis/services/posture-settings-firebase';
import {
    DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
    resolvePostureOrientationStrategyId,
} from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';
import { inferSagittalInputFromCaptured } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-strategy-context';
import { RotateCcw, Loader2, Edit2, LayoutDashboard, ChevronDown } from "lucide-react";
import { ClassificationDetails, selectPosturePhotoTileClassification } from "@/components/posture-analysis/ClassificationDetails";
import { useClient } from '@/context/client-context';
import { useSettings } from '@/components/settings/lib/settings-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { AnnotatedImage } from "@/components/posture-analysis/AnnotatedImage";
import logger from '@/lib/logging';
import { computeSopPosture } from '@/lib/posture-analysis/sop-evaluation';
import type { FormValues } from '@/lib/types';

export default function ClientPostureAnalysisPage() {
    const { id: clientId, analysisId } = useParams<{ id: string, analysisId: string }>();
    const navigate = useNavigate();
    const { setClient } = useClient();
    const { settings } = useSettings();
    const [analysis, setAnalysis] = useState<PostureAnalysis | null>(null);
    const [priorAnalyses, setPriorAnalyses] = useState<{ id: string; label: string; createdAt?: Date | null }[]>([]);
    const [client, setClientState] = useState<Client | null>(null);
    const [loading, setLoading] = useState(true);
    const [isReanalyzing, setIsReanalyzing] = useState(false);
    const { toast } = useToast();
    const { user, loading: authLoading } = useAuth();

    const [calculatedClassification, setCalculatedClassification] = useState<any>(null);
    const [sopCompletionRatio, setSopCompletionRatio] = useState<number>(0);
    const [sopTablePattern, setSopTablePattern] = useState<'APT' | 'SB' | null>(null);
    const [activeView, setActiveView] = useState<View>('Anterior');

    // Ensure activeView resolves to a captured view if 'Anterior' has no image
    useEffect(() => {
        if (!analysis?.captured) return;
        const currentViewOrder: View[] = settings.experimentalFeaturesEnabled
            ? ['Anterior', 'Posterior', 'Left', 'Right']
            : ['Anterior', 'Left', 'Right'];
        const capturedViews = currentViewOrder.filter(v => !!analysis.captured[v]);
        if (capturedViews.length > 0 && !capturedViews.includes(activeView)) {
            setActiveView(capturedViews[0]);
        }
    }, [analysis, settings.experimentalFeaturesEnabled, activeView]);

    useEffect(() => {
        if (!clientId || !analysisId) return;

        if (authLoading || !user) {
            setLoading(false);
            return;
        }

        const fetchAnalysis = async () => {
            setLoading(true);

            const clientData = await getClient(clientId);
            if (clientData) {
                setClientState(clientData);
            }

            const analysisData = await getPostureAnalysis(clientId, analysisId!);

            if (analysisData) {
                setAnalysis(analysisData);
            } else {
                logger.debug("No such document!");
            }

            let ratio = 0;
            let tablePattern: 'APT' | 'SB' | null = null;
            try {
                const assessments = await getClientAssessments(clientId);
                if (assessments && assessments.length > 0) {
                    const latest = assessments[0];
                    if (latest.inputs) {
                        const parsed = parseAssessmentValues(latest.inputs as any, 'current');
                        const totalTests = getSopAssessments('current').length * 2;
                        const completedTests = Object.values(parsed.numeric).filter(v => v !== null).length;
                        ratio = Math.min(1, completedTests / totalTests);
                        const sopPosture = computeSopPosture(latest.inputs as FormValues, null);
                        tablePattern = sopPosture.pattern;
                    }
                }
            } catch (err) {
                logger.error("Error fetching SOP assessments for ratio:", err);
            }
            setSopCompletionRatio(ratio);
            setSopTablePattern(tablePattern);

            setLoading(false);
        };

        const fetchPriorAnalyses = async () => {
            const list = await getClientPostureAnalyses(clientId);
            const formatted = list.map(a => {
                const raw: any = (a as any).date ?? (a as any).createdAt;
                const d = safeToDate(raw);
                return {
                    id: a.id,
                    label: formatDateTimeLocale(d),
                    createdAt: d
                };
            });
            setPriorAnalyses(formatted);
        };

        fetchAnalysis();
        fetchPriorAnalyses();
    }, [clientId, analysisId, authLoading, user]);

    const [orientationStrategyId, setOrientationStrategyId] = useState<PostureOrientationStrategyId>(
        DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID
    );

    useEffect(() => {
        let cancelled = false;
        if (!analysis) {
            setOrientationStrategyId(
                resolvePostureOrientationStrategyId(
                    settings.postureOrientationStrategyId,
                    settings.experimentalFeaturesEnabled === true,
                    true
                )
            );
            return;
        }
        // Saved analyses pin a strategy at calculation time; honour that for historical fidelity.
        if (analysis.postureOrientationStrategyId) {
            setOrientationStrategyId(analysis.postureOrientationStrategyId);
            return;
        }
        // No strategy stored on the analysis: prefer the app-wide setting, then legacy posture-settings doc.
        const fromSettings = resolvePostureOrientationStrategyId(
            settings.postureOrientationStrategyId,
            settings.experimentalFeaturesEnabled === true,
            true
        );
        if (settings.postureOrientationStrategyId || !settings.experimentalFeaturesEnabled) {
            setOrientationStrategyId(fromSettings);
            return;
        }
        void (async () => {
            const s = user?.uid
                ? await loadPostureSettings(user.uid)
                : loadPostureSettingsFromLocalStorage();
            if (!cancelled) {
                setOrientationStrategyId(
                    resolvePostureOrientationStrategyId(
                        s.postureOrientationStrategyId,
                        settings.experimentalFeaturesEnabled === true,
                        true
                    )
                );
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [analysis, user?.uid, settings.postureOrientationStrategyId, settings.experimentalFeaturesEnabled]);

    useEffect(() => {
        if (!analysis) {
            setCalculatedClassification(null);
            return;
        }

        // If classification exists in stored data, use it
        if (analysis.classification) {
            setCalculatedClassification(analysis.classification);
            return;
        }

        // Otherwise, calculate using central evaluation pipeline
        const hasLandmarks = Object.values(analysis.captured || {}).some(
            (c) => c?.landmarks && Array.isArray(c.landmarks) && c.landmarks.length > 0
        );
        if (hasLandmarks) {
            try {
                const evalResult = evaluateFullPostureAnalysis({
                    captured: analysis.captured,
                    clientHeightCM: analysis.clientHeight,
                    unit: analysis.unit,
                    clientSex: 'Unknown',
                    strategyId: orientationStrategyId,
                    sopCompletionRatio,
                    sopTablePosture: { pattern: sopTablePattern },
                });
                setCalculatedClassification(evalResult.classification);
            } catch (err) {
                logger.error('Failed to calculate classification:', err);
                setCalculatedClassification(null);
            }
        } else {
            setCalculatedClassification(null);
        }
    }, [analysis, orientationStrategyId, sopCompletionRatio, sopTablePattern]);

    // Re-analysis runs only when the practitioner presses Re-analyze (Michail, 2026-10-17):
    // opening a saved analysis never recalculates or saves it.
    const handleReanalyze = useCallback(async () => {
        if (!analysis || !clientId || !analysisId) {
            toast({
                title: "Cannot Re-analyze",
                description: "Analysis data is missing.",
                variant: "destructive"
            });
            return;
        }

        // Check if we have landmarks to re-analyze
        const hasLandmarks = Object.values(analysis.captured).some(
            (captured) => captured?.landmarks && Array.isArray(captured.landmarks) && captured.landmarks.length > 0
        );

        if (!hasLandmarks) {
            toast({
                title: "Cannot Re-analyze",
                description: "No landmark data available. Original images may have been uploaded before analysis feature was implemented.",
                variant: "destructive"
            });
            return;
        }

        setIsReanalyzing(true);
        try {
            // Central authoritative evaluation pipeline (manual floor correction applied if set)
            const {
                analysis: recalculatedAnalysis,
                captured: recalculatedCaptured,
                classification,
            } = evaluateFullPostureAnalysis({
                captured: analysis.captured,
                clientHeightCM: analysis.clientHeight,
                unit: analysis.unit,
                clientSex: 'Unknown',
                strategyId: orientationStrategyId,
                sopCompletionRatio,
                sopTablePosture: { pattern: sopTablePattern },
            });

            logger.debug('Final recalculated analysis data:', recalculatedAnalysis);
            logger.debug('Classification result:', classification);

            const hasLateralViews = recalculatedAnalysis['Left'] || recalculatedAnalysis['Right'];
            if (!hasLateralViews) {
                logger.warn('WARNING: No lateral views (Left/Right) found. Classification requires at least one lateral view.');
                toast({
                    title: "Incomplete Data",
                    description: "Classification requires at least one lateral view (Left or Right). Cannot determine pattern without sagittal plane data.",
                    variant: "default"
                });
                setIsReanalyzing(false);
                return;
            }

            if (!classification) {
                logger.error('Classification returned null - likely missing required lateral view data');
                toast({
                    title: "Classification Failed",
                    description: "Could not determine posture pattern. Ensure at least one lateral view (Left or Right) has valid landmark data.",
                    variant: "destructive"
                });
                setIsReanalyzing(false);
                return;
            }

            // Update the analysis in the database
            await updatePostureAnalysis(clientId, analysisId, {
                analysis: recalculatedAnalysis,
                captured: recalculatedCaptured as any,
                classification: classification as any,
                postureOrientationStrategyId: orientationStrategyId,
                _reanalyzedAt: new Date().toISOString(),
            });

            // Reload the analysis to show updated results
            const updatedAnalysis = await getPostureAnalysis(clientId, analysisId);
            if (updatedAnalysis) {
                setAnalysis(updatedAnalysis);
                // Also update calculated classification state
                setCalculatedClassification(classification);
            } else {
                // If reload failed, at least update local state
                setAnalysis({
                    ...analysis,
                    analysis: recalculatedAnalysis,
                    classification: classification,
                    _reanalyzedAt: new Date().toISOString()
                } as PostureAnalysis);
                setCalculatedClassification(classification);
            }

            toast({
                title: "Re-analysis Complete",
                description: `Analysis recalculated. Pattern: ${classification?.pattern || 'Unknown'}`,
                variant: "default"
            });
        } catch (error) {
            logger.error("Failed to re-analyze:", error);
            toast({
                title: "Re-analysis Failed",
                description: error instanceof Error ? error.message : "Could not re-analyze the posture analysis.",
                variant: "destructive"
            });
        } finally {
            setIsReanalyzing(false);
        }
    }, [analysis, clientId, analysisId, client, toast, orientationStrategyId, sopCompletionRatio, sopTablePattern]);


    const detailedAnalysis = useMemo(() => {
        if (!analysis) return null;

        const result: Partial<Record<View, AnalysisResult[]>> = {};
        const { captured, unit } = analysis;
        const experimentalEnabled = settings.experimentalFeaturesEnabled === true;

        for (const view of Object.keys(captured) as View[]) {
            const data = captured[view];
            if (data?.landmarks && data.cmPerPx) {
                let metrics: AnalysisResult[];
                // Same rule as evaluateFullPostureAnalysis: only a manual floor correction applies, about its pivot.
                const { floorCorrectionDeg = 0, floorCorrectionPivot } = pickManualFloorCorrection(data);
                const pivot = floorCorrectionPivot ?? undefined;
                const landmarks = rotateLandmarks(data.landmarks, floorCorrectionDeg, pivot);
                const custom = rotateCustomLandmarks(data.custom, floorCorrectionDeg, pivot);
                if (view === 'Anterior') {
                    metrics = analyzeAnterior(landmarks, view, data.cmPerPx, unit, custom);
                } else if (view === 'Posterior') {
                    metrics = analyzePosterior(landmarks, view, data.cmPerPx, unit, custom);
                } else if (view === 'Left' || view === 'Right') {
                    metrics = analyzeLateral(landmarks, view, data.cmPerPx, unit, custom, {
                        omitPelvicTilt: !!data.pelvisPinsOptOut,
                    });
                } else {
                    continue;
                }
                result[view] = filterMetricsByExperimental(metrics, experimentalEnabled);
            }
        }
        return result;
    }, [analysis, settings.experimentalFeaturesEnabled]);

    const handleExportPdf = async () => {
        if (!analysis) {
            toast({ title: "No analysis data", description: "Cannot export PDF without analysis data.", variant: "destructive" });
            return;
        }
        try {
            await exportPostureAnalysisToPdf(analysis, client);
        } catch (error) {
            logger.error("Failed to export Posture Analysis PDF:", error);
            toast({ title: "Export Failed", description: "Could not generate the PDF for the posture analysis.", variant: "destructive" });
        }
    };

    const handleEdit = useCallback(() => {
        if (!client || !analysisId) return;

        // Ensure client is set in context for PostureAnalysisApp
        // setClient(client); // Removed as per instruction

        // Navigate to the editor with analysisId as search param
        // navigate(`/clients/${clientId}/posture-analysis`); // Removed as per instruction
        // Note: The prompt asked for "edit an existing analysis with the standard UI that is reachable when clicking posture analysis"
        // In App.tsx, /posture-analysis points to PostureAnalysisPage which contains PostureAnalysisApp.
        navigate(`/posture-analysis?analysisId=${analysisId}`);
    }, [client, analysisId, setClient, navigate]);

    if (loading) {
        return (
            <PageLayout
                title="Loading..."
                isLoading={true}
                headerActions={
                    <Button variant="ghost" size="sm" asChild>
                        <Link to={`/clients/${clientId}`}>
                            <ChevronLeft className="mr-2 h-4 w-4" />
                            <span className="hidden sm:inline">Back to Client</span>
                        </Link>
                    </Button>
                }
            >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <Card><CardHeader><Skeleton className="h-6 w-32" /></CardHeader><CardContent><Skeleton className="aspect-square w-full" /></CardContent></Card>
                    <Card><CardHeader><Skeleton className="h-6 w-32" /></CardHeader><CardContent><Skeleton className="aspect-square w-full" /></CardContent></Card>
                </div>
            </PageLayout>
        );
    }

    if (!analysis || !client) {
        return (
            <PageLayout
                title="Analysis Not Found"
                subtitle="The requested posture analysis could not be found"
                headerActions={
                    <Button variant="ghost" size="sm" asChild>
                        <Link to={`/clients/${clientId}`}>
                            <ChevronLeft className="mr-2 h-4 w-4" />
                            <span className="hidden sm:inline">Back to Client</span>
                        </Link>
                    </Button>
                }
            >
                <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">Analysis not found.</p>
                    <Button asChild>
                        <Link to={`/clients/${clientId}`}>Go back to client</Link>
                    </Button>
                </div>
            </PageLayout>
        );
    }

    const viewOrder: View[] = settings.experimentalFeaturesEnabled
      ? ['Anterior', 'Posterior', 'Left', 'Right']
      : ['Anterior', 'Left', 'Right'];

    return (
        <PageLayout
            title="Posture Analysis"
            subtitle={`Analysis from ${formatDateLocale(analysis.createdAt)}`}
            headerActions={
                <Button variant="ghost" size="sm" asChild>
                    <Link to={`/clients/${clientId}`}>
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        <span className="hidden sm:inline">Back to {client ? clientDisplayLabel(client) : 'Client'}</span>
                    </Link>
                </Button>
            }
        >
            <div className="flex flex-col gap-4 md:gap-6">
                {captureLacksLateralView(analysis.captured) && (
                    <p className="text-sm text-foreground">{LATERAL_VIEW_REQUIRED_NOTICE}</p>
                )}
                <ContextualToolbar position="sticky-top">
                    <ToolbarButtonGroup align="right">
                        {priorAnalyses.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger
                                    id="priorAnalyses"
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-transparent hover:bg-muted/50 h-10 mr-2"
                                >
                                    <LayoutDashboard className="w-4 h-4 sm:mr-2 shrink-0" />
                                    <span className="hidden sm:inline">
                                        {priorAnalyses.find(a => a.id === analysisId)?.label ?? 'Select analysis'}
                                    </span>
                                    <ChevronDown className="ml-2 h-4 w-4 opacity-50 hidden sm:block shrink-0" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-56">
                                    {priorAnalyses.map((a) => (
                                        <DropdownMenuItem
                                            key={a.id}
                                            onClick={() => {
                                                if (!a.id || !clientId) return;
                                                navigate(`/clients/${clientId}/posture-analysis/${a.id}`);
                                            }}
                                        >
                                            {a.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        <Button onClick={handleEdit} variant="outline" size="sm" className="h-10">
                            <Edit2 className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Edit</span>
                        </Button>
                        {settings.experimentalFeaturesEnabled && (
                            <Button
                                onClick={() => void handleReanalyze()}
                                variant="outline"
                                size="sm"
                                className="h-10"
                                disabled={isReanalyzing}
                            >
                                {isReanalyzing ? (
                                    <>
                                        <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                                        <span className="hidden sm:inline">Re-analyzing...</span>
                                    </>
                                ) : (
                                    <>
                                        <RotateCcw className="h-4 w-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Re-analyze</span>
                                    </>
                                )}
                            </Button>
                        )}
                        <Button onClick={handleExportPdf} variant="outline" size="sm" className="h-10">
                            <Download className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">PDF</span>
                        </Button>
                    </ToolbarButtonGroup>
                </ContextualToolbar>

                {(analysis.classification || calculatedClassification) && (
                    <Card key={analysisId}>
                        <CardHeader>
                            <CardTitle className="text-foreground">Posture Classification</CardTitle>
                            <CardDescription>
                                {analysis._reanalyzedAt ? (
                                    <span className="text-xs text-muted-foreground">
                                        Re-analyzed: {formatDateTimeLocale(analysis._reanalyzedAt)}
                                    </span>
                                ) : !analysis.classification && calculatedClassification ? (
                                    <span className="text-xs text-warning-text">
                                        Calculated from stored data (not yet saved)
                                    </span>
                                ) : null}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <ClassificationDetails
                                classification={selectPosturePhotoTileClassification(
                                    analysis.classification || calculatedClassification
                                )}
                                showDetailSections
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Visual Posture Analysis Card with view tabs on top */}
                <Card className="border-line bg-surface max-w-4xl mx-auto w-full">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-foreground text-center">Visual Posture Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* View Selector Tabs */}
                        <div className="flex rounded-lg border border-line bg-muted p-1 gap-1 w-full max-w-md mx-auto">
                            {viewOrder.map((view) => {
                                const isActive = activeView === view;
                                const hasPhoto = !!analysis.captured[view];

                                return (
                                    <button
                                        key={view}
                                        type="button"
                                        disabled={!hasPhoto}
                                        onClick={() => setActiveView(view)}
                                        className={cn(
                                            "flex-1 py-2 text-xs font-bold rounded-md transition-all select-none font-['Roboto',sans-serif]",
                                            isActive
                                                ? "bg-surface text-primary border border-primary/20 shadow-sm"
                                                : "text-muted-foreground hover:text-fg disabled:opacity-40"
                                        )}
                                    >
                                        {view}
                                        {!hasPhoto && " (No Photo)"}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Selected View content: side-by-side on desktop, stacked on mobile */}
                        {analysis.captured[activeView] && detailedAnalysis?.[activeView] ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                <div className="p-4 bg-muted rounded-md overflow-hidden flex justify-center w-full">
                                    <AnnotatedImage view={activeView} analysis={analysis} />
                                </div>
                                <div className="w-full">
                                    <AnalysisReportTable data={detailedAnalysis[activeView]!} view={activeView} />
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground text-sm">
                                No image captured for this view.
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </PageLayout>
    );
}
